/**
 * The Movers News cell, the pure half (`docs/plans/movers.md`, column 17).
 *
 * Three headline columns — `preNews`, `postNews`, `news` — each with siblings
 * on the same row: `*Tag`, `*At` (epoch ms) and `*Count`. The cell draws
 * `tag · headline · 07:02 · +3`, muted once the headline is over two hours old,
 * and a click asks the host for the window's whole list. The server builds the
 * columns (`services/qv-data/app/qf/ext_news.py`); this only reads them.
 */

/** The headline columns, and the window each one's list is asked for under. */
export const NEWS_WINDOWS: Readonly<Record<string, "pre" | "post" | "market">> = {
  preNews: "pre",
  postNews: "post",
  news: "market",
};

export type NewsWindow = (typeof NEWS_WINDOWS)[keyof typeof NEWS_WINDOWS];

/** A headline older than this is drawn muted (owner, 2026-10-29: two hours). */
export const NEWS_STALE_MS = 2 * 60 * 60 * 1000;

export function isNewsField(field: string): boolean {
  return Object.prototype.hasOwnProperty.call(NEWS_WINDOWS, field);
}

export interface NewsParts {
  tag: string | null;
  title: string;
  /** `07:02`, New York time — the exchange's clock, as the session times are. */
  time: string | null;
  /** Articles beyond the one shown; 0 draws no `+N`. */
  more: number;
  stale: boolean;
}

const ET_CLOCK = new Intl.DateTimeFormat("en-US", {
  timeZone: "America/New_York",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
});

/** What the cell draws for `field` on `row`, or null for no headline. */
export function newsParts(
  row: Record<string, unknown> | undefined,
  field: string,
  now: number,
): NewsParts | null {
  const title = row?.[field];
  if (typeof title !== "string" || !title) return null;
  const tag = row?.[`${field}Tag`];
  const at = row?.[`${field}At`];
  const count = row?.[`${field}Count`];
  const ms = typeof at === "number" ? at : typeof at === "bigint" ? Number(at) : null;
  const n = typeof count === "number" ? count : typeof count === "bigint" ? Number(count) : 0;
  return {
    tag: typeof tag === "string" && tag ? tag : null,
    title,
    time: ms === null ? null : ET_CLOCK.format(ms),
    more: Math.max(0, n - 1),
    stale: ms !== null && now - ms > NEWS_STALE_MS,
  };
}
